import React, { useCallback, useMemo } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import SimplePageContent from "../../components/SimplePageContent";
import ListItem from "../../ui_atoms/ListItem";
import { useI18n } from "../../hooks/i18n-hooks";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ClientProfile } from "../../__generated__/graphql";
import ListTitle from "../../ui_atoms/ListTitle";
import { useCctLabel } from "../../hooks/cct-utils";
import { ProfileStackParamList } from "../../navigations/ProfileStack";
import Text from "../../components/Text";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";

type IJMConfigurationListScreenProps = StackScreenProps<
    ProfileStackParamList,
    "IJMConfigurationListScreen"
>;

type IjmConfiguration = {
    cctGroups: {
        [key: string]: {
            [workerGroup: string]: {
                ijmGroup: {
                    id: string;
                    name: string;
                };
                payFirstDays: boolean;
                payRemainingPercents: number;
            };
        };
    };
};

type IJMPlan = {
    cctKey: string;
    workerGroupKey: string;
    ijmGroupId: string | null;
    ijmGroupName: string | null;
    ijmPayFirstDays: boolean;
    ijmPayRemainingPercents: number;
};

const IJMConfigurationListScreen: React.FC<IJMConfigurationListScreenProps> = ({ navigation }) => {
    const i18n = useI18n();
    const { fonts, gridSize, colors } = useTheme();
    const currentProfile = useCurrentProfile<ClientProfile>();

    const getCctLabel = useCctLabel(currentProfile?.client?.useLegalEmployer.useLseCct || false);

    const ijmPlans = useMemo<IJMPlan[]>(() => {
        const config = currentProfile?.client?.ijmConfiguration as
            | IjmConfiguration
            | null
            | undefined;

        if (!config || !config.cctGroups) {
            return [];
        }

        const plans: IJMPlan[] = [];

        Object.entries(config.cctGroups).forEach(([cctKey, workerGroups]) => {
            Object.entries(workerGroups).forEach(([workerGroupKey, ijmInfo]) => {
                plans.push({
                    cctKey,
                    workerGroupKey,
                    ijmGroupId: ijmInfo.ijmGroup ? ijmInfo.ijmGroup.id : null,
                    ijmGroupName: ijmInfo.ijmGroup ? ijmInfo.ijmGroup.name : null,
                    ijmPayFirstDays: ijmInfo.payFirstDays,
                    ijmPayRemainingPercents: ijmInfo.payRemainingPercents,
                });
            });
        });

        return plans;
    }, [currentProfile?.client?.ijmConfiguration]);

    const getWorkerGroupLabel = useCallback(
        (workerGroupKey: string): string => {
            if (workerGroupKey === "all") {
                return i18n.t("ijm.configuration_list.all_workers");
            }
            // find group with workerGroupKey to display its name
            const workerGroup = currentProfile.client.workerGroups.find((group) => {
                return group.id === workerGroupKey;
            });
            return i18n.t("ijm.configuration_list.group", {
                group: workerGroup?.name || workerGroupKey,
            });
        },
        [currentProfile, i18n],
    );

    const handlePlanPress = (plan: IJMPlan) => {
        navigation.navigate("IJMSelectionViewer", {
            cctLabel: getCctLabel(plan.cctKey),
            ijmSelection: {
                ijmGroupId: plan.ijmGroupId,
                ijmGroupName: plan.ijmGroupName,
                ijmPayFirstDays: plan.ijmPayFirstDays,
                ijmPayRemainingPercents: plan.ijmPayRemainingPercents,
            },
        });
    };

    // Group plans by CCT
    const plansByCct = useMemo(() => {
        const grouped: { [cctKey: string]: IJMPlan[] } = {};
        ijmPlans.forEach((plan) => {
            if (!grouped[plan.cctKey]) {
                grouped[plan.cctKey] = [];
            }
            grouped[plan.cctKey].push(plan);
        });
        return grouped;
    }, [ijmPlans]);

    if (ijmPlans.length === 0) {
        return (
            <SimplePageContent>
                <VStack style={{ padding: gridSize }} gap={0.5} alignment="stretch">
                    <Text style={{ ...fonts.bodyMedium, color: colors.onSurfaceDisabled }}>
                        {i18n.t("ijm.configuration_list.no_plans")}
                    </Text>
                    <Text style={{ ...fonts.bodyMedium, color: colors.onSurfaceDisabled }}>
                        {i18n.t("ijm.configuration_list.no_plans_description")}
                    </Text>
                </VStack>
            </SimplePageContent>
        );
    }
    return (
        <SimplePageContent>
            {Object.entries(plansByCct).map(([cctKey, plans]) => {
                const cctLabel = getCctLabel(cctKey);
                return (
                    <React.Fragment key={cctKey}>
                        <ListTitle value={cctLabel} />
                        {plans.map((plan, index) => (
                            <ListItem
                                key={`${plan.cctKey}-${plan.workerGroupKey}-${index}`}
                                icon="ijm"
                                title={getWorkerGroupLabel(plan.workerGroupKey)}
                                description={
                                    plan.ijmGroupName || i18n.t("ijm.configuration_list.no_ijm")
                                }
                                onPress={() => handlePlanPress(plan)}
                            />
                        ))}
                    </React.Fragment>
                );
            })}
        </SimplePageContent>
    );
};

export default IJMConfigurationListScreen;
